import { useRef } from 'react';

/**
 * Conserva el último dato bueno de una query sondeada mientras un sondeo falla.
 *
 * ── El fallo que existe para impedir ───────────────────────────────────────
 *
 * Con el `errorPolicy` por defecto, cuando un sondeo de Apollo falla (un bache
 * del hotspot basta) `data` pasa a `undefined`. Para quien lo consume eso se
 * lee como «ya no hay» —no hay tramo activo, no hay recorridos— y no como «no
 * lo sé ahora mismo». En el cronómetro, esa lectura tumbó al detector de
 * cambio de sentido: el tramo activo desaparecía un ciclo, el detector
 * reiniciaba su estado, y el giro real se perdía (C-25, 2026-09-02, 11:26).
 *
 * ── Regla ──────────────────────────────────────────────────────────────────
 *
 * Devuelve el último dato bueno SOLO si hay error Y la clave es la misma.
 *
 *  - Sin error y sin dato (cargando, o `skip`) devuelve `undefined`: aquí sí es
 *    «no lo sé» y no hay nada que fingir.
 *  - Con otra clave no hereda: un cambio de recorrido durante un corte de red
 *    no puede colar el tramo del recorrido anterior como si fuera del nuevo.
 *
 * Es más estrecho a propósito que `previousData` de Apollo, que también guarda
 * el resultado de las variables ANTERIORES y por eso sirve para pintar pero no
 * para decidir.
 */
export function useLastKnownOnError<T>(
  data: T | undefined,
  error: unknown,
  key: string | undefined
): T | undefined {
  const lastGood = useRef<{ key: string | undefined; data: T } | null>(null);

  if (data !== undefined) {
    lastGood.current = { key, data };
    return data;
  }
  if (error && lastGood.current && lastGood.current.key === key) {
    return lastGood.current.data;
  }
  return undefined;
}

export default useLastKnownOnError;
